/* Dependencies */
import { FunctionComponent, useState, useEffect } from 'react';
import { getWindowWidth } from '../../../helpers/getWindowWidth/getWindowWidth';
import { Paragraph } from '../../Atoms/Paragraph/Paragraph';
import { Modal } from '../Modal/Modal';

// Models
import { HotspotProps, hotspotThemes } from './Hotspot.model';

/**
 * Hotspot -For animated sections.
 * @param props - Required component props.
 * @returns
 */
export const Hotspot: FunctionComponent<HotspotProps> = ({
  className,
  title,
  subheading,
  side = 'Right',
  index = 0,
  theme = 'Primary',
  textSize = 'Large',
}) => {
  const [animated, setanimated] = useState(false);
  const [openModal, setopenModal] = useState(false);
  const [isDeskop, setisDeskop] = useState(false);

  const pathStyle = animated
    ? hotspotThemes[theme].iconAnimated
    : hotspotThemes[theme].icon;

  const animate = () => {
    setanimated(!animated);
  };
  const modalHandler = () => {
    if (getWindowWidth() >= 1024) {
      setisDeskop(true);
      setopenModal(false);
    } else if (getWindowWidth() <= 1024 && animated && !isDeskop) {
      setopenModal(true);
    } else {
      setopenModal(false);
      setanimated(false);
      setisDeskop(false);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setTimeout(() => {
        modalHandler();
      }, 500);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    modalHandler();
  }, [animated]);

  return (
    <div className={`w-[60px] lg:w-[47.5%] ${className || ''}`}>
      <div
        className={`${
          side == 'Right' ? 'ml-auto pl-12' : 'pr-12'
        } hidden max-w-max lg:block`}
      >
        <Paragraph
          className={`font-display-a font-medium text-primary ${
            textSize === 'Large' ? '' : 'text-[18px]'
          } ${
            animated
              ? 'animate-fadeInUp opacity-0'
              : 'opacity-1 animate-fadeOutDown'
          }`}
        >
          {title}
        </Paragraph>
        <Paragraph
          className={`text-xs text-primary lg:text-2xl ${
            animated
              ? 'animate-fadeInUp opacity-0'
              : 'opacity-1 animate-fadeOutDown'
          }`}
        >
          {subheading}
        </Paragraph>
        <div className="absolute left-0 bottom-8 h-px w-full bg-transparent">
          <span
            className={`linear absolute inset-y-0 ${
              side == 'Right' ? 'left-0 right-full' : 'right-0 left-full'
            } ${animated && side == 'Right' ? 'lg:right-0' : ''}
            ${animated && side == 'Left' ? 'lg:left-0' : ''} 
            block ${
              hotspotThemes[theme].backgroundAnimated
            } transition-all duration-500`}
          ></span>
        </div>
      </div>
      <div
        className={`max-w-max ${side == 'Left' ? 'ml-auto' : ''}`}
        data-aos="zoom-in"
        data-aos-delay={((index + 1) * 50).toString()}
      >
        <div
          onClick={() => animate()}
          className={`${
            animated
              ? `${hotspotThemes[theme].backgroundAnimated} hover:animate-none`
              : `${hotspotThemes[theme].background} hover:animate-beat`
          } flex h-[40px] w-[40px] cursor-pointer items-center justify-center rounded-full shadow-hotspot transition-colors duration-700 md:h-[60px] md:w-[60px]`}
        >
          <svg
            width="38"
            height="37"
            viewBox="0 0 38 37"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 md:h-9 md:w-9"
          >
            <path
              className={`${pathStyle} `}
              d="M32.7539 9.35615C32.4271 10.1299 32.9455 10.5689 33.7118 11.071C35.2234 12.0748 36.3891 13.499 37.0571 15.1578C37.725 16.8166 37.864 18.6329 37.456 20.3698C37.0571 22.1204 36.1295 23.7146 34.7924 24.9474C33.4553 26.1801 31.7701 26.995 29.9535 27.287C29.0023 27.4324 28.0338 27.4324 27.0826 27.287C26.877 27.2568 26.5586 27.3007 26.5502 26.966C26.5502 26.5599 26.4487 26.1428 26.6149 25.7532C26.6854 25.5913 26.8967 25.723 27.0629 25.7532C28.1783 25.9596 29.3269 25.921 30.425 25.6402C31.5231 25.3593 32.543 24.8433 33.4103 24.1298C34.2776 23.4163 34.9705 22.5233 35.4384 21.5159C35.9063 20.5084 36.1374 19.412 36.115 18.3065C36.0445 15.1648 34.4246 12.9149 31.503 11.5841C31.055 11.3811 30.8747 11.2466 31.0071 10.7006C31.3647 9.2353 31.2542 7.69856 30.6902 6.29612C30.1263 4.89368 29.136 3.69247 27.8518 2.85331C26.5797 1.99698 25.0679 1.54264 23.5215 1.55197C21.9752 1.56129 20.4693 2.03384 19.2082 2.90544C19.0883 3.01908 18.9276 3.08265 18.7602 3.08265C18.5929 3.08265 18.4322 3.01908 18.3123 2.90544C17.0431 2.02249 15.5233 1.54579 13.9636 1.54137C12.4038 1.53694 10.8812 2.00501 9.60675 2.88075C8.32301 3.73865 7.34049 4.95954 6.79184 6.37856C6.24318 7.79758 6.15481 9.34639 6.53868 10.8159C6.63447 11.2055 6.59505 11.3921 6.16963 11.5759C3.09593 12.8984 1.17451 16.0346 1.55203 19.0363C1.99435 22.5567 4.38627 25.1249 7.86003 25.7614C8.80725 25.9423 9.78323 25.9236 10.7224 25.7066C11.0746 25.6215 11.2126 25.6846 11.2267 26.0852C11.2577 27.2321 11.2605 27.2129 10.0998 27.3337C8.86627 27.4581 7.61966 27.3371 6.43561 26.978C5.25157 26.619 4.15481 26.0294 3.21185 25.245C2.2689 24.4605 1.49942 23.4977 0.950152 22.4149C0.400886 21.332 0.083256 20.1518 0.0165693 18.9458C-0.175009 15.5078 1.29845 12.7887 4.24256 10.8707C4.4493 10.7669 4.61595 10.6007 4.71753 10.3969C4.81911 10.1931 4.85015 9.96271 4.80602 9.74029C4.58345 6.32972 5.88227 3.54475 8.78412 1.61584C11.686 -0.313064 14.8104 -0.499643 17.9883 0.973788C18.222 1.10582 18.4873 1.17535 18.7574 1.17535C19.0276 1.17535 19.2929 1.10582 19.5265 0.973788C25.6542 -2.01972 32.7933 2.41155 32.7539 9.35615Z"
            />
            <path
              className={`${pathStyle} `}
              d="M14.0059 29.8909C14.0059 28.0963 13.9845 26.3018 14.0059 24.5073C14.0059 24.0014 13.9017 23.6499 13.4152 23.4636C12.576 23.1414 12.306 22.5476 12.3915 21.6584C12.477 20.7691 12.3915 19.8425 12.4183 18.9346C12.4355 18.7854 12.4076 18.6344 12.338 18.5011C12.2685 18.3677 12.1605 18.2582 12.028 18.1864C11.5952 17.9257 11.2655 17.5247 11.0942 17.0506C10.9229 16.5765 10.9204 16.0582 11.0872 15.5825C11.234 15.1134 11.5338 14.7065 11.939 14.4262C12.3443 14.146 12.8318 14.0086 13.3244 14.0356C13.8055 14.0256 14.2767 14.1724 14.6665 14.4535C15.0562 14.7346 15.3433 15.1347 15.484 15.5931C15.6482 16.0696 15.643 16.5878 15.4693 17.0609C15.2956 17.534 14.9639 17.9333 14.5298 18.1918C14.3892 18.2689 14.2751 18.3862 14.2022 18.5286C14.1293 18.671 14.101 18.8319 14.1209 18.9905C14.1369 19.7653 14.1476 20.5428 14.1209 21.3176C14.1048 21.7196 14.2037 22.0072 14.5966 22.1536C15.5695 22.521 15.7754 23.2186 15.7593 24.2197C15.6925 27.9472 15.7192 31.6748 15.7593 35.389C15.7593 35.9481 15.5963 36.0999 15.0483 36.0812C14.0113 36.044 14.0113 36.0812 14.0113 35.0668L14.0059 29.8909ZM13.3431 16.8445C13.4848 16.8445 13.6208 16.7884 13.721 16.6886C13.8213 16.5887 13.8776 16.4532 13.8776 16.312C13.8848 16.2337 13.8744 16.1547 13.8473 16.0808C13.8201 16.007 13.7769 15.94 13.7207 15.8847C13.6644 15.8295 13.5966 15.7874 13.5221 15.7613C13.4475 15.7353 13.3682 15.7261 13.2896 15.7343C13.2103 15.7305 13.1312 15.7444 13.0579 15.775C12.9847 15.8055 12.9192 15.8519 12.8662 15.9108C12.8132 15.9697 12.7741 16.0396 12.7516 16.1154C12.7292 16.1913 12.7239 16.2712 12.7363 16.3493C12.7497 16.6794 12.9742 16.8419 13.3431 16.8472V16.8445Z"
            />
            <path
              className={`${pathStyle} `}
              d="M21.9268 29.4059C21.9268 27.4841 21.9514 25.5596 21.9268 23.635C21.9268 23.0795 22.0473 22.6967 22.5867 22.4894C23.2986 22.2236 23.567 21.7345 23.4574 20.9955C23.4136 20.5766 23.4136 20.1544 23.4574 19.7355C23.504 18.9965 23.4164 18.4224 22.6989 17.8748C21.6392 17.0773 21.757 15.4824 22.7674 14.5999C23.0092 14.3977 23.2898 14.2441 23.5932 14.1479C23.8965 14.0518 24.2165 14.015 24.5346 14.0397C24.8527 14.0644 25.1626 14.1502 25.4463 14.292C25.7301 14.4338 25.982 14.6288 26.1875 14.8658C26.3936 15.1037 26.5457 15.381 26.6342 15.6797C26.7226 15.9784 26.7454 16.2919 26.701 16.5997C26.6566 16.9075 26.546 17.2028 26.3764 17.4665C26.2068 17.7301 25.982 17.9562 25.7165 18.13C25.5415 18.2207 25.3987 18.3605 25.3069 18.5313C25.215 18.702 25.1784 18.8957 25.2017 19.0869C25.2291 20.1236 25.1771 21.1656 25.2237 22.2023C25.2483 22.7339 25.0922 23.0928 24.5884 23.2921C23.8928 23.558 23.6929 24.0417 23.7039 24.786C23.7505 28.3001 23.7039 31.8169 23.7395 35.331C23.7395 35.9078 23.6053 36.1603 22.992 36.062C22.8331 36.0392 22.6716 36.0392 22.5128 36.062C22.0664 36.1151 21.9322 35.945 21.9377 35.5038C21.9268 33.4756 21.9268 31.4421 21.9268 29.4059ZM24.3501 15.727C23.9613 15.7695 23.7258 15.9769 23.7313 16.3277C23.7312 16.4001 23.7463 16.4717 23.7757 16.5382C23.8051 16.6047 23.8481 16.6646 23.9022 16.7144C23.9563 16.7642 24.0202 16.8027 24.0902 16.8276C24.1602 16.8526 24.2346 16.8634 24.3091 16.8594C24.3833 16.8684 24.4587 16.862 24.5302 16.8407C24.6018 16.8193 24.6678 16.7834 24.724 16.7354C24.7801 16.6874 24.8252 16.6284 24.8561 16.5623C24.887 16.4961 24.9031 16.4243 24.9033 16.3517C24.9389 15.9795 24.6815 15.7988 24.3474 15.727H24.3501Z"
            />
            <path
              className={`${pathStyle} `}
              d="M17.949 27.2113C17.949 24.4732 17.949 21.7377 17.949 19.0023C17.9718 18.8322 17.9411 18.6595 17.8608 18.5063C17.7806 18.3531 17.6545 18.2264 17.4987 18.1426C17.0686 17.879 16.7429 17.4847 16.5736 17.0225C16.4043 16.5603 16.4012 16.057 16.5647 15.5929C16.7099 15.136 17.006 14.7369 17.4083 14.4559C17.8106 14.1749 18.2972 14.0274 18.7945 14.0355C19.3032 14.009 19.8066 14.1455 20.2257 14.4236C20.6448 14.7016 20.9558 15.1055 21.11 15.5717C21.281 16.034 21.2842 16.5382 21.1192 17.0025C20.9542 17.4668 20.6306 17.8639 20.2009 18.1294C20.0381 18.2198 19.9066 18.3545 19.8231 18.5163C19.7395 18.6782 19.7076 18.8602 19.7312 19.0394C19.7312 24.4918 19.7312 29.9415 19.7505 35.3938C19.7505 35.959 19.5876 36.15 19.0239 36.0625C18.8646 36.0386 18.7024 36.0386 18.5431 36.0625C18.0623 36.1314 17.9242 35.9537 17.9297 35.4867C17.9711 32.7274 17.949 29.968 17.949 27.2113ZM18.8636 16.8399C18.9382 16.8431 19.0128 16.8318 19.0827 16.8066C19.1527 16.7814 19.2166 16.7428 19.2705 16.6932C19.3245 16.6436 19.3675 16.584 19.3968 16.518C19.4261 16.452 19.4412 16.381 19.4411 16.3093C19.4501 16.2308 19.4408 16.1515 19.4138 16.077C19.3869 16.0024 19.3429 15.9346 19.2852 15.8784C19.2275 15.8223 19.1575 15.7792 19.0803 15.7523C19.003 15.7254 18.9205 15.7153 18.8388 15.7229C18.7639 15.7193 18.689 15.7304 18.6188 15.7554C18.5485 15.7805 18.4843 15.819 18.43 15.8686C18.3757 15.9183 18.3325 15.978 18.303 16.0442C18.2736 16.1103 18.2584 16.1816 18.2585 16.2535C18.2519 16.3317 18.263 16.4103 18.291 16.4839C18.319 16.5576 18.3633 16.6246 18.4207 16.6802C18.4782 16.7359 18.5476 16.779 18.624 16.8066C18.7004 16.8342 18.7822 16.8455 18.8636 16.8399Z"
            />
          </svg>
        </div>

        <Modal
          title={`${title}`}
          description={`${subheading}`}
          isOpen={openModal}
          onClose={() => setanimated(false)}
        />
      </div>
    </div>
  );
};
